'use client'

import { AddIcon } from '@repo/ui/AddIcon'
import { Chat } from '@repo/ui/Chat'
import { confirm } from '@repo/ui/Confirm'
import { DocumentIcon } from '@repo/ui/DocumentIcon'
import { DraftClaimCard, DraftClaim } from '@repo/ui/DraftClaimCard'
import { Drawer } from '@repo/ui/Drawer'
import { EmptyState } from '@repo/ui/EmptyState'
import { ExpenseClaimForm } from '@repo/ui/ExpenseClaimForm'
import { OvertimeClaimForm } from '@repo/ui/OvertimeClaimForm'
import { formatCurrency } from '@repo/utils/numbers'
import { getRelativeDate } from '@repo/utils/time'
import { LoadingState } from '@repo/ui/LoadingState'
import { Pill } from '@repo/ui/Pill'
import { useAuditHistoryStore } from '@repo/store/useAuditHistoryStore'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useClaimsStore } from '@repo/store/useClaimsStore'
import { useCommentsStore } from '@repo/store/useCommentsStore'
import { useEffect, useState } from 'react'
import { useExpensesStore } from '@repo/store/useExpensesStore'
import { useOvertimesStore } from '@repo/store/useOvertimesStore'
import { useTheme } from '@mui/material/styles'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIDivider from '@mui/material/Divider'
import MUIStack from '@mui/material/Stack'
import MUITab from '@mui/material/Tab'
import MUITabs from '@mui/material/Tabs'
import MUITypography from '@mui/material/Typography'

interface Message {
  id: string | number
  message: string
  senderUsername: string
  timestamp: string
}

interface ClaimsDrawerProps {
  messages?: Message[]
  onSendMessage?: (message: string) => void
  onAddClaim?: () => void
  onEditClaim?: (claim: DraftClaim) => void
  onDeleteClaim?: (claimId: string) => void
  onSave?: (claims: DraftClaim[]) => Promise<void>
  onCancel?: () => void
}

const statusConfig: Record<number, { label: string; color: string; backgroundColor: string }> = {
  0: { label: 'DRAFT', color: 'black', backgroundColor: '#DFE0E4' },
  1: { label: 'SUBMITTED', color: 'black', backgroundColor: '#DFE0E4' },
  2: { label: 'APPROVED', color: 'white', backgroundColor: '#377F86' },
  3: { label: 'REJECTED', color: 'white', backgroundColor: '#d32f2f' },
  4: { label: 'ACTION REQUIRED', color: 'white', backgroundColor: '#DE6A1E' },
  5: { label: 'READY TO PAY', color: 'white', backgroundColor: '#5073C3' },
  6: { label: 'POSTED', color: 'white', backgroundColor: '#647495' },
  7: { label: 'PAID', color: 'white', backgroundColor: '#051C33' }
}

export const ClaimsDrawer = ({ onSave, onCancel }: ClaimsDrawerProps) => {
  const {
    fetchExpenseClaimAuditHistoryByExpenseClaimId,
    fetchOvertimeClaimAuditHistoryByOvertimeClaimId,
    reset: resetAuditHistory
  } = useAuditHistoryStore()
  const { userId } = useAuthenticationStore()
  const {
    comments,
    createExpensesClaimComment,
    updateExpensesClaimComment,
    deleteExpensesClaimComment,
    fetchAllExpensesClaimCommentsByExpenseClaimId,
    createOvertimeClaimComment,
    updateOvertimeClaimComment,
    deleteOvertimeClaimComment,
    fetchAllOvertimeClaimCommentsByOvertimeClaimId,
    reset: resetComments
  } = useCommentsStore()
  const {
    closeClaimsDrawer,
    editMode,
    initialDrawerTab,
    isClaimsDrawerOpen,
    isExpensesTable,
    isNewClaimMode,
    isSubmissionVersion,
    openDirectToForm,
    selectedClaimRow,
    setIsClaimsDrawerOpen
  } = useClaimsStore()
  const { fetchDraftExpenseClaims, deleteExpenseClaimWithLocation, updateExpenseClaimStatuses, expensesTableUpdate } =
    useExpensesStore()
  const { fetchDraftOvertimeClaims, deleteOvertimeClaim, updateOvertimeClaimStatuses, overtimesTableUpdate } =
    useOvertimesStore()
  const [selectedTab, setSelectedTab] = useState('chat')
  const [isSaving, setIsSaving] = useState(false)
  const [showClaimForm, setShowClaimForm] = useState(false)
  const [editingClaim, setEditingClaim] = useState<DraftClaim | null>(null)
  const [claims, setClaims] = useState([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [auditHistory, setAuditHistory] = useState([])

  const theme = useTheme()

  const expenseClaimId = isExpensesTable ? selectedClaimRow?.expenseClaimId : null
  const overtimeClaimId = !isExpensesTable ? selectedClaimRow?.overtimeClaimId : null

  const status = selectedClaimRow?.status ?? 0
  const [pillConfig, setPillConfig] = useState(statusConfig[status] || statusConfig[0])

  useEffect(() => {
    if (isClaimsDrawerOpen && initialDrawerTab) {
      setSelectedTab(initialDrawerTab)
    } else if (isClaimsDrawerOpen && editMode) {
      setSelectedTab('details')
    }
  }, [isClaimsDrawerOpen, editMode, initialDrawerTab])

  useEffect(() => {
    if (isClaimsDrawerOpen && openDirectToForm) {
      setEditingClaim(null)
      setShowClaimForm(true)
    }
  }, [isClaimsDrawerOpen, openDirectToForm])

  useEffect(() => {
    setPillConfig(statusConfig[status] || statusConfig[0])
  }, [status])

  useEffect(() => {
    if (isClaimsDrawerOpen && selectedTab === 'chat') {
      if (isExpensesTable && expenseClaimId) {
        setCommentsLoading(true)
        fetchAllExpensesClaimCommentsByExpenseClaimId({ expenseClaimId }).finally(() => {
          setCommentsLoading(false)
        })
      } else if (!isExpensesTable && overtimeClaimId) {
        setCommentsLoading(true)
        fetchAllOvertimeClaimCommentsByOvertimeClaimId({ overtimeClaimId }).finally(() => {
          setCommentsLoading(false)
        })
      }
    }
    if (!isClaimsDrawerOpen) {
      resetComments()
    }
  }, [
    isClaimsDrawerOpen,
    selectedTab,
    expenseClaimId,
    overtimeClaimId,
    isExpensesTable,
    fetchAllExpensesClaimCommentsByExpenseClaimId,
    fetchAllOvertimeClaimCommentsByOvertimeClaimId,
    resetComments
  ])

  useEffect(() => {
    if (isClaimsDrawerOpen && selectedTab === 'history') {
      const fetchHistory = async () => {
        setHistoryLoading(true)
        try {
          let response
          if (isExpensesTable && expenseClaimId) {
            response = await fetchExpenseClaimAuditHistoryByExpenseClaimId({ expenseClaimId })
          } else if (!isExpensesTable && overtimeClaimId) {
            response = await fetchOvertimeClaimAuditHistoryByOvertimeClaimId({ overtimeClaimId })
          }
          setAuditHistory(response || [])
        } catch (err) {
          console.error('Error fetching audit history:', err)
        } finally {
          setHistoryLoading(false)
        }
      }

      if ((isExpensesTable && expenseClaimId) || (!isExpensesTable && overtimeClaimId)) {
        fetchHistory()
      }
    }
    if (!isClaimsDrawerOpen) {
      resetAuditHistory()
    }
  }, [
    isClaimsDrawerOpen,
    selectedTab,
    expenseClaimId,
    overtimeClaimId,
    isExpensesTable,
    fetchExpenseClaimAuditHistoryByExpenseClaimId,
    fetchOvertimeClaimAuditHistoryByOvertimeClaimId,
    resetAuditHistory
  ])

  const mappedComments: Message[] = (comments || []).map((comment: any) => ({
    id: isExpensesTable ? comment.expenseClaimCommentId : comment.overtimeClaimCommentId,
    message: comment.comment,
    senderUsername: comment.createdBy,
    timestamp: comment.createdDate
  }))

  useEffect(() => {
    if (!isNewClaimMode) return

    const fetchDraftClaims = async () => {
      try {
        if (isExpensesTable) {
          const response = await fetchDraftExpenseClaims({
            pageSize: 25,
            startAtIndex: 0,
            status: 0
          })
          setClaims(response.rows)
        } else {
          const response = await fetchDraftOvertimeClaims({
            pageSize: 25,
            startAtIndex: 0,
            status: 0
          })
          setClaims(response?.rows || [])
        }
      } catch (err) {
        console.error('Error fetching draft claims:', err)
      }
    }

    fetchDraftClaims()
  }, [
    fetchDraftExpenseClaims,
    fetchDraftOvertimeClaims,
    isExpensesTable,
    expensesTableUpdate,
    overtimesTableUpdate,
    isClaimsDrawerOpen,
    isNewClaimMode
  ])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue)
  }

  const handleSendComment = async (message: string) => {
    const now = new Date().toISOString()
    if (isExpensesTable && expenseClaimId) {
      await createExpensesClaimComment({
        expenseClaimId,
        comment: message,
        createdDate: now,
        createdBy: userId,
        updatedDate: now,
        updatedBy: userId
      })
      await fetchAllExpensesClaimCommentsByExpenseClaimId({ expenseClaimId })
    } else if (!isExpensesTable && overtimeClaimId) {
      await createOvertimeClaimComment({
        overtimeClaimId,
        comment: message,
        createdDate: now,
        createdBy: userId,
        updatedDate: now,
        updatedBy: userId
      })
      await fetchAllOvertimeClaimCommentsByOvertimeClaimId({ overtimeClaimId })
    }
  }

  const handleEditMessage = async (messageId: string | number, newMessage: string) => {
    const now = new Date().toISOString()
    if (isExpensesTable && expenseClaimId) {
      await updateExpensesClaimComment({
        expenseClaimCommentId: messageId,
        expenseClaimId,
        comment: newMessage,
        updatedDate: now,
        updatedBy: userId
      })
      await fetchAllExpensesClaimCommentsByExpenseClaimId({ expenseClaimId })
    } else if (!isExpensesTable && overtimeClaimId) {
      await updateOvertimeClaimComment({
        overtimeClaimCommentId: messageId,
        overtimeClaimId,
        comment: newMessage,
        updatedDate: now,
        updatedBy: userId
      })
      await fetchAllOvertimeClaimCommentsByOvertimeClaimId({ overtimeClaimId })
    }
  }

  const handleDeleteMessage = async (messageId: string | number) => {
    const proceed = await confirm('Are you sure you want to delete this comment?')
    if (!proceed) return

    if (isExpensesTable && expenseClaimId) {
      await deleteExpensesClaimComment({ expenseClaimCommentId: messageId })
      await fetchAllExpensesClaimCommentsByExpenseClaimId({ expenseClaimId })
    } else if (!isExpensesTable && overtimeClaimId) {
      await deleteOvertimeClaimComment({ overtimeClaimCommentId: messageId })
      await fetchAllOvertimeClaimCommentsByOvertimeClaimId({ overtimeClaimId })
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    closeClaimsDrawer()
  }

  const handleSave = async () => {
    if (claims.length > 0) {
      setIsSaving(true)
      try {
        if (isExpensesTable) {
          const expenseClaimIds = claims
            .map((claim: any) => claim.expenseClaimId || claim.expensesClaimId || claim.id)
            .filter((id: string | undefined): id is string => id !== undefined)

          if (expenseClaimIds.length > 0) {
            await updateExpenseClaimStatuses({
              status: 1,
              expenseClaimIds: expenseClaimIds
            })
          }
        } else {
          const overtimeClaimIds = claims
            .map((claim: any) => claim.overtimeClaimId || claim.id)
            .filter((id: string | undefined): id is string => id !== undefined)

          if (overtimeClaimIds.length > 0) {
            await updateOvertimeClaimStatuses({
              status: 1,
              overtimeClaimIds: overtimeClaimIds
            })
          }
        }

        if (onSave) {
          await onSave(claims as DraftClaim[])
        }
        closeClaimsDrawer()
      } catch (error) {
        console.error('Error saving claims:', error)
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleDeleteClaim = async (claimId: string) => {
    const proceed = await confirm('Are you sure you want to delete this claim?')
    if (!proceed) return
    await deleteExpenseClaimWithLocation({ expenseClaimId: claimId })
  }

  const handleEditClaim = (claim: DraftClaim) => {
    setEditingClaim(claim)
    setShowClaimForm(true)
  }

  const handleAddNewClaim = () => {
    setEditingClaim(null)
    setShowClaimForm(true)
  }

  const handleClose = () => {
    setShowClaimForm(false)
    setEditingClaim(null)
    if (isNewClaimMode) {
      closeClaimsDrawer()
    } else {
      setIsClaimsDrawerOpen()
    }
  }

  // Check if we're editing an existing claim from the table
  const isEditingFromTable = selectedClaimRow && !isNewClaimMode

  // Get the claim data to edit (either from table row or from draft claims)
  const claimToEdit = isEditingFromTable ? selectedClaimRow : editingClaim

  const getDrawerTitle = () => {
    if (showClaimForm || isEditingFromTable) {
      if (editingClaim || isEditingFromTable) {
        return isExpensesTable ? 'Edit expense claim' : 'Edit overtime claim'
      }
      return isExpensesTable ? 'New expense claim' : 'New overtime claim'
    }
    if (isNewClaimMode) {
      return isExpensesTable ? 'New expenses claim' : 'New overtime claim'
    }
    return isExpensesTable ? 'Expense claim details' : 'Overtime claim details'
  }

  const drawerTitle = getDrawerTitle()

  const total = claims.reduce(
    (total: number, claim: any) => total + (isExpensesTable ? claim.grossAmount || 0 : claim.amount || 0),
    0
  )

  return (
    <div data-testid="ClaimsDrawer">
      <Drawer
        isOpen={isClaimsDrawerOpen}
        onClose={handleClose}
        headingNode={
          <MUIStack direction="row" spacing={2} alignItems="center" justifyContent={'space-between'}>
            <MUITypography variant="h5" sx={{ mb: 0, alignItems: 'center', display: 'flex' }}>
              {drawerTitle}
            </MUITypography>
          </MUIStack>
        }
        sx={{ '& > .MuiStack-root': { mb: 0 } }}
      >
        {showClaimForm && !isEditingFromTable ? (
          <MUIBox sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {isExpensesTable ? (
              <ExpenseClaimForm
                editingClaim={claimToEdit}
                isSubmissionVersion={isSubmissionVersion}
                onSaveSuccess={() => {
                  setShowClaimForm(false)
                  setEditingClaim(null)
                }}
              />
            ) : (
              <OvertimeClaimForm
                editingClaim={claimToEdit}
                isSubmissionVersion={isSubmissionVersion}
                onSaveSuccess={() => {
                  setShowClaimForm(false)
                  setEditingClaim(null)
                }}
              />
            )}
          </MUIBox>
        ) : isNewClaimMode && !isEditingFromTable ? (
          <MUIStack sx={{ px: 4, flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {claims.length > 0 && (
              <MUITypography variant="h6" color="text.primary" sx={{ textTransform: 'uppercase', px: 2, mb: 0, mt: 4 }}>
                Draft claims for submission
              </MUITypography>
            )}
            <MUIBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100% - 100px)',
                justifyContent: 'flex-start',
                alignItems: 'center',
                my: 4
              }}
            >
              <MUIBox sx={{ flex: 1, width: '100%', overflow: 'auto', px: 2 }}>
                {claims.length === 0 ? (
                  <EmptyState
                    icon={<DocumentIcon sx={{ width: 48, height: 48, color: theme.palette.secondary.main, mb: 4 }} />}
                    heading="No claims yet"
                    text="Click the button below to add your first claim"
                    sx={{
                      minHeight: 0,
                      height: 'auto',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      pt: 8,
                      pb: 4
                    }}
                  />
                ) : (
                  <MUIStack spacing={2} sx={{ width: '100%' }}>
                    {(claims as DraftClaim[]).map((claim: any) => (
                      <DraftClaimCard
                        key={isExpensesTable ? claim.expenseClaimId : claim.overtimeClaimId}
                        claim={claim}
                        onEdit={handleEditClaim}
                        onDelete={() =>
                          isExpensesTable
                            ? deleteExpenseClaimWithLocation({ expenseClaimId: claim.expenseClaimId })
                            : deleteOvertimeClaim({ overtimeClaimId: claim.overtimeClaimId })
                        }
                      />
                    ))}
                  </MUIStack>
                )}
                <MUIButton
                  variant="text"
                  startIcon={
                    <AddIcon
                      sx={{
                        width: 24,
                        height: 24,
                        color: theme.palette.secondary.main
                      }}
                    />
                  }
                  onClick={handleAddNewClaim}
                  sx={{
                    color: theme.palette.secondary.main,
                    textTransform: 'none',
                    fontWeight: 500,
                    justifyContent: 'flex-start',
                    pl: 0,
                    mb: 2,
                    mt: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: theme.palette.secondary.dark,
                      '& .MuiSvgIcon-root': {
                        '& path': {
                          stroke: theme.palette.secondary.dark,
                          transition: 'stroke 0.3s ease'
                        }
                      }
                    },
                    '& .MuiButton-icon': {
                      ml: 0
                    },
                    '& .MuiSvgIcon-root': {
                      '& path': {
                        transition: 'stroke 0.3s ease'
                      }
                    }
                  }}
                >
                  Add another {isExpensesTable ? 'expense' : 'overtime'} claim
                </MUIButton>
                {claims.length > 0 && (
                  <>
                    <MUIDivider sx={{ my: 2, mb: 4, mt: 1 }} />
                    <MUIStack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2, width: '100%' }}
                    >
                      <MUITypography variant="body1" sx={{ fontWeight: 500 }}>
                        Total
                      </MUITypography>
                      <MUITypography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(total)}
                      </MUITypography>
                    </MUIStack>
                    <MUIStack direction="row" spacing={2} justifyContent="flex-end" sx={{ width: '100%', mt: 8 }}>
                      <MUIButton
                        variant="text"
                        onClick={handleCancel}
                        sx={{
                          textTransform: 'uppercase',
                          mr: '0.5rem !important',
                          '&:hover': {
                            backgroundColor: 'transparent'
                          }
                        }}
                      >
                        Cancel
                      </MUIButton>
                      <MUIButton
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving}
                        sx={{
                          textTransform: 'uppercase',
                          backgroundColor: theme.palette.success.light,
                          '&:hover': {
                            // @ts-ignore
                            backgroundColor: theme.palette.success.lightHover
                          },
                          '&:disabled': {
                            backgroundColor: '#B0BEC5'
                          }
                        }}
                      >
                        {isSaving ? 'Submitting...' : 'Submit all'}
                      </MUIButton>
                    </MUIStack>
                  </>
                )}
              </MUIBox>
            </MUIBox>
          </MUIStack>
        ) : (
          <MUIBox sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <MUITabs
              value={selectedTab}
              onChange={handleTabChange}
              aria-label="Claims drawer tabs"
              sx={{
                padding: '0 16px',
                minHeight: '22px',
                '& > div > div': {
                  display: 'flex',
                  flexDirection: 'row',
                  height: '2.75rem'
                },
                '& .MuiTab-root': {
                  backgroundColor: '#647495',
                  borderRadius: '4px',
                  color: '#fff !important',
                  height: '22px',
                  margin: '0.75rem 0.25rem',
                  maxWidth: 'none',
                  minHeight: '22px',
                  minWidth: 0,
                  padding: '0 8px',
                  textTransform: 'none',
                  transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
                  whiteSpace: 'pre',
                  width: 'auto',
                  '&[tabindex="-1"]': {
                    backgroundColor: 'transparent',
                    color: '#051c33 !important',
                    transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out'
                  }
                },
                '& .MuiTabs-indicator': {
                  display: 'none'
                }
              }}
            >
              <MUITab label="Approval status" value="chat" />
              <MUITab label="Claim details" value="details" />
              <MUITab label="History" value="history" />
            </MUITabs>

            <MUIBox sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              {selectedTab === 'chat' && (
                <>
                  <MUIStack direction="row" justifyContent="space-between" alignItems={'baseline'}>
                    <MUITypography variant="h6" sx={{ px: '1rem', pt: '1rem', mb: 0 }}>
                      Approval status
                    </MUITypography>
                    <Pill
                      label={pillConfig.label}
                      color={pillConfig.color}
                      backgroundColor={pillConfig.backgroundColor}
                      sx={{ height: '25px', mr: 4, [`& span`]: { fontSize: '0.75rem' } }}
                    />
                  </MUIStack>
                  {commentsLoading ? (
                    <LoadingState sx={{ height: '100%' }} />
                  ) : (
                    <Chat
                      messages={mappedComments}
                      sendHandler={handleSendComment}
                      onEditMessage={handleEditMessage}
                      onDeleteMessage={handleDeleteMessage}
                    />
                  )}
                </>
              )}
              {selectedTab === 'details' && (
                <MUIBox sx={{ height: '100%', overflow: 'auto' }}>
                  {isExpensesTable ? (
                    <ExpenseClaimForm editingClaim={claimToEdit} isSubmissionVersion={isSubmissionVersion} />
                  ) : (
                    <OvertimeClaimForm editingClaim={claimToEdit} isSubmissionVersion={isSubmissionVersion} />
                  )}
                </MUIBox>
              )}

              {selectedTab === 'history' && (
                <MUIBox sx={{ padding: '16px' }}>
                  {historyLoading ? (
                    <LoadingState sx={{ height: '100%' }} />
                  ) : (
                    <MUIStack spacing={2}>
                      {(auditHistory || []).map((item: any, index: number) => (
                        <MUIBox key={index}>
                          <MUITypography variant="body2" sx={{ mb: 0 }}>
                            {item.description}
                          </MUITypography>
                          <MUITypography variant="caption" color="text.secondary">
                            {getRelativeDate(item.createdDate)}
                          </MUITypography>
                        </MUIBox>
                      ))}
                    </MUIStack>
                  )}
                </MUIBox>
              )}
            </MUIBox>
          </MUIBox>
        )}
      </Drawer>
    </div>
  )
}
